/* AgentMeeting — Room variation A: chat-stream (Linear/Vercel-style transcript)
 *
 * Wired to the live backend:
 *   - reads localStorage `agentmeeting.currentMeetingId` to pick the active meeting
 *   - GETs meeting record + messages on mount
 *   - subscribes via `useMeetingStream` for turn.start / turn.token / turn.end /
 *     agenda.advance / meeting.state events
 *   - falls back to a "no meeting selected" empty state when no id is present
 */
import React from 'react';
import { AGENDA, AGENTS, AgentT, STANCE_COLOR } from '../shared/data';
import {
  Avatar, StanceTag, ModelChip, RoundSteps, CostMeter, RoleSwitcher,
  AsciiSep, renderInline, I, Role,
} from '../shared/ui';
import { meetings as meetingsApi, HTTPError, type ServerEvent } from '../shared/api';
import { useMeetingStream } from '../shared/useMeetingStream';
import { useAuth } from '../shared/auth';
import { useI18n } from '../shared/i18n';

type RoomProps = {
  role: Role;
  meetingType: string;
  embedded?: boolean;
  topBar?: React.ReactNode;
};

interface ServerMsg {
  id: string;
  round_no: number;
  participant_id: string;
  kind: string;
  content: string;
  tokens_in?: number;
  tokens_out?: number;
  cost_cents?: number;
  parent_id?: string | null;
  created_at?: string;
  speaker_name?: string;
  speaker_short?: string;
  speaker_stance?: string;
  speaker_model?: string;
  _streaming?: boolean;
}

function synthAgent(m: ServerMsg | null | undefined): AgentT {
  const safe = m || ({} as ServerMsg);
  const stance = (safe.speaker_stance || 'neutral') as AgentT['stance'];
  const nameRaw = safe.speaker_name || '';
  const fallbackShort = nameRaw
    ? nameRaw.trim().slice(0, 2).toUpperCase()
    : '··';
  return {
    id: safe.participant_id || safe.id || 'unknown',
    name: nameRaw || '—',
    short: safe.speaker_short || fallbackShort || '··',
    stance,
    stanceLabel: '',
    model: (safe.speaker_model || null) as AgentT['model'],
    persona: '',
  };
}

function fmtTs(iso?: string): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour12: false });
  } catch { return ''; }
}

function goMeetings() {
  if (typeof window !== 'undefined') location.hash = '#/app/meetings';
}

export function RoomChatStream({ role, meetingType, embedded, topBar }: RoomProps) {
  useAuth();
  const { t } = useI18n();
  const meetingId = typeof window !== 'undefined'
    ? localStorage.getItem('agentmeeting.currentMeetingId')
    : null;

  const [serverMessages, setServerMessages] = React.useState<ServerMsg[] | null>(null);
  const [meetingMeta, setMeetingMeta] = React.useState<any>(null);
  const [loadErr, setLoadErr] = React.useState<string | null>(null);
  const [draft, setDraft] = React.useState('');

  React.useEffect(() => {
    if (!meetingId) return;
    meetingsApi.get(meetingId)
      .then(setMeetingMeta)
      .catch((e: unknown) => setLoadErr(e instanceof HTTPError ? `meeting ${e.status}` : `${e}`));
    meetingsApi.messages(meetingId)
      .then(r => setServerMessages((r.messages || []) as ServerMsg[]))
      .catch((e: unknown) => setLoadErr(e instanceof HTTPError ? `messages ${e.status}` : `${e}`));
  }, [meetingId]);

  // Subscribe to live stream.
  const streamOpts = {
    onTurnToken: (ev: ServerEvent) => {
      const { message_id, delta } = ev.payload || {};
      if (!message_id) return;
      setServerMessages(prev => (prev || []).map(m =>
        m.id === message_id ? { ...m, content: (m.content || '') + (delta || '') } : m
      ));
    },
    onTurnEnd: (ev: ServerEvent) => {
      const p = ev.payload || {};
      setServerMessages(prev => (prev || []).map(m =>
        m.id === p.message_id ? {
          ...m,
          content: p.content ?? m.content,
          tokens_in: p.tokens_in ?? m.tokens_in,
          tokens_out: p.tokens_out ?? m.tokens_out,
          cost_cents: p.cost_cents ?? m.cost_cents,
          _streaming: false,
        } : m
      ));
    },
    onMeetingState: (_ev: ServerEvent) => { /* banner could be added later */ },
    onAgendaAdvance: (ev: ServerEvent) => {
      setMeetingMeta((prev: any) => ({ ...(prev || {}), current_round: ev.payload?.round_no }));
    },
  };
  const { events, connected } = useMeetingStream(meetingId, streamOpts as any);

  // The hook doesn't surface `turn.start` directly; watch the events array.
  const lastSeenIdx = React.useRef(0);
  React.useEffect(() => {
    for (let i = lastSeenIdx.current; i < events.length; i++) {
      const ev = events[i];
      if (ev.type === 'turn.start') {
        const p = ev.payload || {};
        if (!p.message_id) continue;
        setServerMessages(prev => {
          const list = prev || [];
          if (list.some(m => m.id === p.message_id)) return list;
          return [...list, {
            id: p.message_id,
            round_no: p.round_no ?? 0,
            participant_id: p.speaker_id || '',
            speaker_name: p.speaker_name,
            speaker_short: p.speaker_short,
            speaker_stance: p.speaker_stance,
            speaker_model: p.speaker_model,
            kind: 'speech',
            content: '',
            _streaming: true,
            created_at: new Date().toISOString(),
          }];
        });
      }
    }
    lastSeenIdx.current = events.length;
  }, [events]);

  // Pause stub for director.
  const onPause = async () => {
    if (!meetingId) return;
    try { await meetingsApi.pause(meetingId); }
    catch (e) { setLoadErr(`${e}`); }
  };

  // ── Empty state ────────────────────────────────────────────
  if (!meetingId) {
    return (
      <div className="frame room-frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="col gap-3" style={{
          maxWidth: 420, padding: 24, border: '1px solid var(--line-2)',
          borderRadius: 6, background: 'var(--bg-1)', textAlign: 'center',
        }}>
          <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>NO MEETING SELECTED</div>
          <div style={{ fontSize: 15 }}>Pick a meeting to enter the room.</div>
          <button className="btn primary" onClick={goMeetings}>Open meetings →</button>
        </div>
      </div>
    );
  }

  // ── Loading state ──────────────────────────────────────────
  if (serverMessages == null) {
    return (
      <div className="frame room-frame col" style={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        {topBar}
        <div className="mono tiny dim">loading transcript…</div>
        {loadErr && <div className="mono tiny" style={{ color: 'var(--err)', marginTop: 8 }}>{loadErr}</div>}
      </div>
    );
  }

  const liveMsg = serverMessages.find(m => m._streaming);
  const liveParticipantId = liveMsg?.participant_id;

  const currentRound = meetingMeta?.current_round ?? 0;
  const totalRounds = meetingMeta?.total_rounds ?? Math.max(currentRound + 1, 6);

  // Group messages by round_no.
  const byRound = new Map<number, ServerMsg[]>();
  for (const m of serverMessages) {
    const arr = byRound.get(m.round_no) || [];
    arr.push(m);
    byRound.set(m.round_no, arr);
  }
  const rounds = Array.from(byRound.keys()).sort((a, b) => a - b);

  // Derive participants from server messages (dedupe by participant_id).
  // Fall back to mock AGENTS only if server messages haven't surfaced any speakers yet.
  const participantsFromServer: AgentT[] = (() => {
    const seen = new Map<string, AgentT>();
    for (const m of serverMessages) {
      const pid = m.participant_id;
      if (!pid) continue;
      if (seen.has(pid)) continue;
      seen.set(pid, synthAgent(m));
    }
    return Array.from(seen.values());
  })();
  const sidebarAgents: AgentT[] = participantsFromServer.length > 0
    ? participantsFromServer
    : AGENTS.filter(a => a.id !== 'U1');

  return (
    <div className="frame room-frame" style={{ height: '100%' }}>
      {topBar}
      {!embedded && (
        <div className="term-bar">
          <div className="lights"><span /><span /><span /></div>
          <div className="crumbs">
            <span>~/meetings</span>
            <span className="sep">/</span>
            <span>{meetingMeta?.title || meetingId.slice(0, 8)}</span>
            <span className="sep">/</span>
            <span className="here">room</span>
          </div>
          <div className="right">
            <CostMeter
              tokens={Math.round((meetingMeta?.spent_cents || 0) * 40)}
              cents={meetingMeta?.spent_cents || 0}
              budget={meetingMeta?.budget_cents || 500}
            />
            <RoleSwitcher role={role} onChange={() => {}} compact />
          </div>
        </div>
      )}

      <div className="room-meta-strip" style={{
        padding: '14px 22px 12px', display: 'flex', alignItems: 'flex-start', gap: 18,
        borderBottom: '1px solid var(--line-1)',
      }}>
        <div className="col gap-1 grow">
          <div className="meta">
            <span className="dim">[{(meetingMeta?.type || meetingType).toUpperCase()}]</span>{' '}议题 · motion
          </div>
          <div style={{ fontSize: 17, fontWeight: 500, letterSpacing: '-0.01em' }}>
            {meetingMeta?.topic || meetingMeta?.title || '(untitled)'}
          </div>
          <div className="meta dim" style={{ marginTop: 2 }}>
            {meetingMeta?.type || meetingType} · {connected ? 'live' : 'connecting…'}
            {loadErr && <> · <span style={{ color: 'var(--err)' }}>{loadErr}</span></>}
          </div>
        </div>
        <div className="col gap-2 room-meta-right" style={{ alignItems: 'flex-end' }}>
          <RoundSteps current={currentRound} total={totalRounds} />
          <div className="flex gap-1">
            <span className={'dot ' + (connected ? 'live pulse' : '')} />
            <span className="mono tiny live-fg" style={{ letterSpacing: '0.06em' }}>
              {connected ? 'LIVE' : 'OFFLINE'}
            </span>
          </div>
        </div>
      </div>

      <div className="grow flex room-body" style={{ minHeight: 0 }}>
        <div className="grow scroll col" data-room-transcript style={{ padding: '4px 0 12px' }}>
          {rounds.length === 0 && (
            <div className="meta dim" style={{ padding: 32, textAlign: 'center' }}>
              (no turns yet — waiting for first speaker)
            </div>
          )}
          {rounds.map(rno => {
            const agendaEntry = AGENDA[rno];
            const label = agendaEntry
              ? `R${rno + 1} · ${agendaEntry.label} · ${agendaEntry.rule}`
              : `R${rno + 1}`;
            return (
              <React.Fragment key={rno}>
                <AsciiSep label={label} total={70} />
                {(byRound.get(rno) || []).map(m => (
                  <ChatRow key={m.id} msg={m} />
                ))}
              </React.Fragment>
            );
          })}
          <div style={{ height: 24 }} />
          {liveMsg && (
            <AsciiSep label={`SPEAKING · ${liveMsg.speaker_name || '…'}`} total={70} />
          )}
          <div style={{ height: 24 }} />
        </div>

        <div className="vr room-side-vr" />
        <div className="col room-side" style={{ width: 240, flex: '0 0 240px', background: 'var(--bg-1)' }}>
          <div className="col" style={{ padding: '14px 14px 8px' }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.room.participants')}</div>
          </div>
          <div className="col gap-1" style={{ padding: '0 8px' }}>
            {sidebarAgents.map(a => {
              if (!a) return null;
              const isSpeaking = !!liveParticipantId && (
                a.id === liveParticipantId
                || a.name === liveMsg?.speaker_name
                || (!!a.short && a.short === liveMsg?.speaker_short)
              );
              return (
                <div key={a.id} className="hov flex center gap-2" style={{
                  padding: '7px 8px', borderRadius: 4,
                  background: isSpeaking ? 'var(--bg-3)' : 'transparent',
                }}>
                  <Avatar agent={a} size="sm" />
                  <div className="col grow" style={{ minWidth: 0 }}>
                    <div className="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</div>
                    <div className="flex center gap-2">
                      <StanceTag agent={a} />
                      {a.model && <ModelChip modelId={a.model} />}
                    </div>
                  </div>
                  {isSpeaking && <span className="dot live pulse" />}
                </div>
              );
            })}
          </div>

          <div className="hr" style={{ margin: '12px 0' }} />

          <div className="col gap-1" style={{ padding: '0 14px' }}>
            <div className="mono tiny dim" style={{ letterSpacing: '0.08em', marginBottom: 6 }}>{t('app.room.agenda')}</div>
            {AGENDA.map(a => {
              const status: 'done' | 'cur' | 'next' =
                a.round < currentRound ? 'done'
                : a.round === currentRound ? 'cur'
                : 'next';
              return (
                <div key={a.round} className="flex center gap-2 sm" style={{ padding: '3px 0' }}>
                  <span className="mono dim" style={{ width: 22 }}>r{a.round + 1}</span>
                  <span style={{
                    color: status === 'cur' ? 'var(--live)' : status === 'done' ? 'var(--fg-2)' : 'var(--fg-3)',
                    textDecoration: status === 'done' ? 'line-through' : 'none',
                    textDecorationColor: 'var(--fg-4)',
                  }}>{a.label}</span>
                  {status === 'cur' && <span className="dot live pulse" style={{ marginLeft: 'auto' }} />}
                </div>
              );
            })}
          </div>

          <div className="grow" />

          <div className="col gap-2" style={{ padding: 12, borderTop: '1px solid var(--line-1)' }}>
            {role === 'director' ? (
              <>
                <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>{t('app.room.director.controls')}</div>
                <div className="flex gap-1 wrap">
                  <button className="btn" style={{ flex: 1 }} onClick={onPause}>{I.pause} {t('app.room.director.pause')}</button>
                  <button className="btn" disabled title="rewind — coming next wave">{I.rewind}</button>
                  <button className="btn" disabled title="fork — coming next wave">{I.fork}</button>
                </div>
                <button className="btn ghost sm" disabled title="call on next — coming next wave">{t('app.room.callOn')} →</button>
              </>
            ) : role === 'host' ? (
              <>
                <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>HOST</div>
                <button className="btn primary" style={{ width: '100%' }} disabled title="advance — coming next wave">{t('app.room.advanceRound')} →</button>
                <button className="btn ghost" style={{ width: '100%' }} disabled>{t('app.room.callOn')}</button>
                <button className="btn ghost" style={{ width: '100%' }} disabled>{t('app.room.openVote')}</button>
              </>
            ) : (
              <>
                <div className="mono tiny dim" style={{ letterSpacing: '0.08em' }}>JOIN AS PARTICIPANT</div>
                <button className="btn primary" style={{ width: '100%' }} disabled title="mic — coming next wave">{I.mic} {t('app.room.requestMic')}</button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="col room-composer" style={{
        padding: 14, gap: 8,
        borderTop: '1px solid var(--line-1)', background: 'var(--bg-1)',
      }}>
        <div className="flex center gap-2 mono tiny dim wrap">
          <span>$</span>
          <span>speak as</span>
          <span className="fg-1">[{role.toUpperCase()}]</span>
          <span>·</span>
          <span>human injection — coming next wave</span>
        </div>
        <div className="flex gap-2 center">
          <textarea className="input mono" rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="human injection — coming next wave" disabled />
          <button className="btn primary" disabled
            title="Human injection — coming next wave"
            style={{ alignSelf: 'stretch', padding: '0 18px', opacity: 0.5, cursor: 'not-allowed' }}>
            {I.send}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChatRow({ msg }: { msg: ServerMsg }) {
  if (!msg) return null;
  const a = synthAgent(msg);
  if (!a || !a.short) return null;
  const ts = fmtTs(msg.created_at);
  const c = STANCE_COLOR[a.stance] || 'var(--fg-2)';

  return (
    <div className="col">
      <div className="flex gap-3" style={{ padding: '10px 22px 12px', alignItems: 'flex-start' }}>
        {a && <Avatar agent={a} size="sm" />}
        <div className="col grow gap-1" style={{ minWidth: 0 }}>
          <div className="flex center gap-2 wrap">
            <span className="sm" style={{ fontWeight: 500 }}>{a.name}</span>
            <span className="tag" style={{ borderColor: 'transparent', color: c, padding: '2px 0' }}>
              <span className="dot" style={{ background: c }} />
              <span style={{ letterSpacing: '0.04em' }}>{a.stance}</span>
            </span>
            <span className="dim">·</span>
            {a.model && (
              <>
                <ModelChip modelId={a.model} withCost costCents={msg.cost_cents || 0} />
                <span className="dim">·</span>
              </>
            )}
            <span className="mono tiny dim">{ts}</span>
            {msg.parent_id && (
              <>
                <span className="dim">·</span>
                <span className="mono tiny dim">↪ {msg.parent_id.slice(0, 6)}</span>
              </>
            )}
          </div>
          <div className="md" style={{ lineHeight: 1.55, color: 'var(--fg-1)' }}>
            {renderInline(msg.content || '')}
            {msg._streaming && <span className="caret" />}
          </div>
        </div>
      </div>
    </div>
  );
}
